import { useEffect, useState, useCallback } from 'react';
import { cashRegisterAPI } from './api';
import logger from './logger';

/**
 * Guards the browser tab against being closed while a cash register is still
 * open. When the user hits "Close tab" the browser shows its native
 * confirmation dialog ("Are you sure you want to leave?"). If the caja is
 * already closed, no prompt is shown.
 *
 * We can't customize the message — the browser owns that string for security
 * reasons — but we can control WHEN it's triggered.
 *
 * Strategy:
 * - Fetch cash-registers on mount and refresh whenever the tab regains focus
 *   (`visibilitychange`). Skip polling to keep the app quiet.
 * - Consider any register with `status === 'open'` as "unsaved".
 * - Attach a beforeunload listener only while there's an open register.
 */
export default function useUnsavedCashRegisterGuard({ enabled = true } = {}) {
  const [hasOpenRegister, setHasOpenRegister] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await cashRegisterAPI.getAll();
      const open = Array.isArray(data) && data.some((r) => r.status === 'open');
      setHasOpenRegister(open);
    } catch (err) {
      // Fail-open: if we can't check, don't block the user.
      logger.error('Cash-register guard: failed to fetch status', err);
      setHasOpenRegister(false);
    }
  }, [enabled]);

  // Refresh on mount and whenever the tab regains visibility (covers the case
  // where the caja was closed from another tab).
  useEffect(() => {
    refresh();
    const onVisibility = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [refresh]);

  // Attach beforeunload only while we know a register is open. The handler
  // must set `returnValue` (deprecated but required for cross-browser
  // support) AND call `preventDefault()` to actually surface the prompt.
  useEffect(() => {
    if (!hasOpenRegister) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // The string is ignored by modern browsers (Chrome/Firefox show a
      // generic message) but is still required by the spec.
      e.returnValue = 'Aún tienes una caja abierta. ¿Seguro que deseas salir?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasOpenRegister]);

  return { hasOpenRegister, refresh };
}
